import React, { useEffect, useState, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import FirebaseContext from "../../firebase/context"; // ✅ corrección aquí
import useToast from "../hooks/useToast";
import FileUploader from "react-firebase-file-uploader";

const EditarInsumos = () => {
  const { firebase } = useContext(FirebaseContext);
  const { regionId, insumoId } = useParams();
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();

  const [insumo, setInsumo] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);

  // Cargar insumo desde Firestore
  useEffect(() => {
    const obtenerInsumo = async () => {
      try {
        const doc = await firebase.db
          .collection(`productos_${regionId}`)
          .doc(insumoId)
          .get();

        if (doc.exists) {
          setInsumo({ id: doc.id, ...doc.data() });
        } else {
          console.log("❌ No se encontró el insumo");
          navigate(`/catalogo/${regionId}`);
        }
      } catch (error) {
        console.error("Error cargando insumo:", error);
      }
    };

    obtenerInsumo();
  }, [firebase, regionId, insumoId, navigate]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      nombre: insumo?.nombre || "",
      precio: insumo?.precio || "",
      categoria: insumo?.categoria || "",
      unidadMedida: insumo?.unidadMedida || "",
      imagen: insumo?.imagen || "",
    },
    validationSchema: Yup.object({
      nombre: Yup.string()
        .min(3, "El insumo debe tener al menos 3 caracteres")
        .required("El nombre es obligatorio"),
      precio: Yup.number()
        .min(0, "El precio no puede ser negativo")
        .required("El precio es obligatorio"),
      categoria: Yup.string().required("La categoría es obligatoria"),
    }),

    onSubmit: async (valores) => {
      try {
        const valoresLimpios = { ...valores };

        Object.keys(valoresLimpios).forEach(
          (k) => valoresLimpios[k] === "" && delete valoresLimpios[k]
        );

        await firebase.db
          .collection(`productos_${regionId}`)
          .doc(insumoId)
          .set(valoresLimpios, { merge: true });

        showSuccess("✅ Insumo actualizado correctamente");
        navigate(`/catalogo/${regionId}`);
      } catch (error) {
        console.error(error);
        showError("❌ Error al actualizar el insumo");
      }
    }
  });

  // Handlers de FileUploader
  const handleUploadStart = () => {
    setUploading(true);
    setProgress(0);
  };

  const handleProgress = (progress) => setProgress(progress);

  const handleUploadError = (error) => {
    setUploading(false);
    console.error(error);
    showError("❌ Error subiendo la imagen");
  };

  const handleUploadSuccess = async (filename) => {
    try {
      setProgress(100);
      setUploading(false);

      // Si había imagen anterior, eliminarla
      if (formik.values.imagen) {
        const prevRef = firebase.storage.refFromURL(formik.values.imagen);
        await prevRef.delete().catch(() => {
          console.warn("⚠️ No se pudo eliminar la imagen anterior, puede que no existiera");
        });
      }

      // Obtener URL de la nueva imagen
      const downloadURL = await firebase.storage
        .ref(`insumos/${filename}`)
        .getDownloadURL();

      formik.setFieldValue("imagen", downloadURL);
      showSuccess("✅ Imagen subida correctamente");
    } catch (error) {
      console.error(error);
      showError("❌ Error procesando la imagen");
    }
  };

  if (!insumo) return <p className="text-gray-600">Cargando datos...</p>;

  return (
    <div className="max-w-2xl mx-auto mt-8">
      <h1 className="text-2xl font-bold mb-4">Editar Insumo</h1>

      <form onSubmit={formik.handleSubmit} className="space-y-4">
        {/* Nombre */}
        <div>
          <label className="block text-gray-700 font-bold">Nombre</label>
          <input
            type="text"
            name="nombre"
            value={formik.values.nombre}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            className="w-full border p-2 rounded"
          />
          {formik.touched.nombre && formik.errors.nombre && (
            <p className="text-red-500">{formik.errors.nombre}</p>
          )}
        </div>

        {/* Precio */}
        <div>
          <label className="block text-gray-700 font-bold">Precio</label>
          <input
            type="number"
            name="precio"
            value={formik.values.precio}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            className="w-full border p-2 rounded"
          />
          {formik.touched.precio && formik.errors.precio && (
            <p className="text-red-500">{formik.errors.precio}</p>
          )}
        </div>

        {/* Categoría */}
        <div>
          <label className="block text-gray-700 font-bold">Categoría</label>
          <select
            name="categoria"
            value={formik.values.categoria}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            className="w-full border p-2 rounded"
          >
            <option value="">Seleccione una categoría</option>
            <option value="Harina">Harina</option>
            <option value="Manteca">Manteca</option>
            <option value="Azucar">Azúcar</option>
            <option value="Aceite">Aceite</option>
            <option value="Levadura">Levadura</option>
            <option value="Mejorador">Mejorador</option>
            <option value="Sal">Sal</option>
            <option value="Ajonjoli">Ajonjolí</option>
            <option value="Scencia">Esencia</option>
            <option value="Margarina">Margarina</option>
            <option value="Manjar">Manjar</option>
          </select>
          {formik.touched.categoria && formik.errors.categoria && (
            <p className="text-red-500">{formik.errors.categoria}</p>
          )}
        </div>

        {/* Imagen */}
        <div>
          <label className="block text-gray-700 font-bold">Imagen</label>

          <FileUploader
            accept="image/*"
            name="imagen"
            randomizeFilename
            storageRef={firebase.storage.ref("insumos")}
            onUploadStart={handleUploadStart}
            onUploadError={handleUploadError}
            onUploadSuccess={handleUploadSuccess}
            onProgress={handleProgress}
          />

          {uploading && <p>Cargando imagen... {progress}%</p>}

          {formik.values.imagen && (
            <img
              src={formik.values.imagen}
              alt="Vista previa"
              className="h-24 mt-2"
            />
          )}
        </div>

        {/* Unidad de Medida */}
        <div>
          <label className="block text-gray-700 font-bold">Unidad de Medida</label>
          <textarea
            name="unidadMedida"
            value={formik.values.unidadMedida}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            className="w-full border p-2 rounded h-24"
          />
          {formik.touched.unidadMedida && formik.errors.unidadMedida && (
            <p className="text-red-500">{formik.errors.unidadMedida}</p>
          )}
        </div>

        {/* Botón */}
        <button
          type="submit"
          className="bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded w-full"
        >
          Guardar Cambios
        </button>
      </form>
    </div>
  );
};

export default EditarInsumos;
